import React, { useEffect, useMemo, useState } from "react";
import {
  useActiveLicenseOverview,
  useExpiringSoonLicenseOverview,
  useMultiCompanyLicenseOverview,
} from "../hooks/useLicenseOverview";
import type { ActiveLicenseOverviewItem } from "../api/licenseOverviewApi";
import { CustomerLicenseTrendChart } from "./CustomerLicenseTrendChart";
import { ChevronDown, ChevronRight } from "lucide-react";

interface Props {
  companyName?: string;
  companyPartnerId?: number;
  companyNames?: string[];
  companyPartnerIds?: number[];
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 10;
const FIXED_BRANDS = [
  "Autodesk",
  "Bluebeam",
  "SketchUp",
  "Chaosgroup",
  "Adobe",
] as const;
const BRAND_CARD_ORDER = [...FIXED_BRANDS, "Others"] as const;

interface BrandProductBreakdown {
  productModel: string;
  totalSeats: number;
  totalBilling: number;
}

interface BrandCardData {
  brand: (typeof BRAND_CARD_ORDER)[number];
  totalSeats: number;
  totalBilling: number;
  products: BrandProductBreakdown[];
}

const formatNumber = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    value
  );
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  return value.slice(0, 10);
};

const isValidCompanyName = (value?: string): value is string => {
  return !!value && !value.startsWith("Company #");
};

const toNumber = (value: number | null | undefined): number => value ?? 0;

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return `HK$ ${formatNumber(value)}`;
};

const formatPercent = (value: number): string => {
  if (!Number.isFinite(value)) return "0.0%";
  return `${value.toFixed(1)}%`;
};

const displayValueFromUnknown = (
  value: unknown,
  fallback: string = "-"
): string => {
  if (Array.isArray(value)) {
    const label = value[1] ?? value[0];
    if (typeof label === "string" && label.trim()) return label.trim();
    if (label != null) return String(label);
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const label = record.label ?? record.name ?? record.display_name;
    if (typeof label === "string" && label.trim()) return label.trim();
    if (label != null) return String(label);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;

    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        return displayValueFromUnknown(JSON.parse(trimmed), fallback);
      } catch {
        return trimmed;
      }
    }

    return trimmed;
  }

  if (value == null) return fallback;
  return String(value);
};

const normalizeBrand = (brandGrouping: unknown) => {
  const normalizedInput = displayValueFromUnknown(brandGrouping, "Others")
    .trim()
    .toLowerCase();
  const matchedBrand = FIXED_BRANDS.find(
    (brand) => brand.toLowerCase() === normalizedInput
  );
  return matchedBrand ?? "Others";
};

const buildBrandCards = (items: ActiveLicenseOverviewItem[]): BrandCardData[] => {
  const grouped = new Map<
    BrandCardData["brand"],
    {
      totalSeats: number;
      totalBilling: number;
      products: Map<string, BrandProductBreakdown>;
    }
  >();

  BRAND_CARD_ORDER.forEach((brand) => {
    grouped.set(brand, {
      totalSeats: 0,
      totalBilling: 0,
      products: new Map<string, BrandProductBreakdown>(),
    });
  });

  items.forEach((item) => {
    const brand = normalizeBrand(item.brand_grouping);
    const group = grouped.get(brand);

    if (!group) return;

    const productModel =
      displayValueFromUnknown(item.product_model, "Unspecified product") ||
      "Unspecified product";
    const seats = toNumber(item.sum_qty);
    const billing = toNumber(item.sum_pip_total);
    const existingProduct = group.products.get(productModel);

    group.totalSeats += seats;
    group.totalBilling += billing;

    if (existingProduct) {
      existingProduct.totalSeats += seats;
      existingProduct.totalBilling += billing;
      return;
    }

    group.products.set(productModel, {
      productModel,
      totalSeats: seats,
      totalBilling: billing,
    });
  });

  return BRAND_CARD_ORDER.map((brand) => {
    const group = grouped.get(brand)!;
    return {
      brand,
      totalSeats: group.totalSeats,
      totalBilling: group.totalBilling,
      products: Array.from(group.products.values()).sort((left, right) =>
        left.productModel.localeCompare(right.productModel)
      ),
    };
  });
};

const SimplePager: React.FC<{
  page: number;
  pageSize: number;
  total: number;
  onChange: (nextPage: number, nextPageSize: number) => void;
}> = ({ page, pageSize, total, onChange }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const isFirst = page <= 1;
  const isLast = page >= pages;

  return (
    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
      <span>
        Page {page} / {pages} - Total: {total}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded border px-2 py-0.5 disabled:opacity-50"
          disabled={isFirst}
          onClick={() => onChange(page - 1, pageSize)}
        >
          Prev
        </button>
        <button
          type="button"
          className="rounded border px-2 py-0.5 disabled:opacity-50"
          disabled={isLast}
          onClick={() => onChange(page + 1, pageSize)}
        >
          Next
        </button>
        <select
          className="h-7 rounded border bg-background px-1"
          value={pageSize}
          onChange={(event) => onChange(1, Number(event.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export const CustomerLicenseOverview: React.FC<Props> = ({
  companyName,
  companyPartnerId,
  companyNames,
  companyPartnerIds,
}) => {
  const [expiringPage, setExpiringPage] = useState<number>(1);
  const [expiringPageSize, setExpiringPageSize] =
    useState<number>(DEFAULT_PAGE_SIZE);
  const [expandedBrand, setExpandedBrand] = useState<
    BrandCardData["brand"] | null
  >(null);

  const selectedCompanyNames = useMemo(() => {
    const values = companyNames?.length ? companyNames : companyName ? [companyName] : [];
    return Array.from(
      new Set(values.map((value) => value.trim()).filter(Boolean))
    );
  }, [companyName, companyNames]);
  const selectedCompanyPartnerIds = useMemo(() => {
    const values = companyPartnerIds?.length ? companyPartnerIds : companyPartnerId ? [companyPartnerId] : [];
    return Array.from(
      new Set(values.filter((value): value is number => typeof value === "number" && value > 0))
    );
  }, [companyPartnerId, companyPartnerIds]);
  const isMultiCompany =
    selectedCompanyNames.length > 1 || selectedCompanyPartnerIds.length > 1;
  const companyFilter = selectedCompanyNames[0]?.trim();
  const companyLabel = useMemo(() => {
    if (!selectedCompanyNames.length) return null;
    if (selectedCompanyNames.length === 1) return selectedCompanyNames[0];
    return `${selectedCompanyNames.length} companies selected`;
  }, [selectedCompanyNames]);
  const canLoad = isMultiCompany
    ? selectedCompanyNames.length > 0 && selectedCompanyPartnerIds.length > 0
    : isValidCompanyName(companyFilter);

  useEffect(() => {
    setExpiringPage(1);
  }, [companyFilter, selectedCompanyNames.join("|"), selectedCompanyPartnerIds.join("|")]);

  const multiOverview = useMultiCompanyLicenseOverview(
    isMultiCompany ? selectedCompanyNames : undefined
  );

  const {
    data: activeData,
    isLoading: isActiveLoading,
    error: activeError,
  } = useActiveLicenseOverview({
    company: isMultiCompany ? undefined : companyFilter,
  });

  const {
    data: expiringData,
    isLoading: isExpiringLoading,
    error: expiringError,
  } = useExpiringSoonLicenseOverview({
    company: isMultiCompany ? undefined : companyFilter,
    page: expiringPage,
    pageSize: expiringPageSize,
  });

  const activeRows = isMultiCompany
    ? multiOverview.activeItems
    : activeData?.items ?? [];
  const expiringRows = isMultiCompany
    ? multiOverview.expiringItems
    : expiringData?.items ?? [];
  const isOverviewLoading = isMultiCompany
    ? multiOverview.isLoading
    : isActiveLoading || isExpiringLoading;
  const overviewError = isMultiCompany
    ? multiOverview.error
    : activeError || expiringError;

  const activeBrandCards = useMemo(
    () => buildBrandCards(activeRows),
    [activeRows]
  );
  const activeTotalBilling = useMemo(
    () =>
      activeRows.reduce(
        (sum, item) => sum + toNumber(item.sum_pip_total),
        0
      ),
    [activeRows]
  );
  const visibleExpiringRows = useMemo(() => {
    if (isMultiCompany) {
      return expiringRows.slice(
        (expiringPage - 1) * expiringPageSize,
        expiringPage * expiringPageSize
      );
    }
    return expiringRows;
  }, [expiringPage, expiringPageSize, expiringRows, isMultiCompany]);

  useEffect(() => {
    if (activeBrandCards.length === 0) {
      setExpandedBrand(null);
      return;
    }

    setExpandedBrand((current) => {
      if (current && activeBrandCards.some((card) => card.brand === current)) {
        return current;
      }

      const topBrand = [...activeBrandCards].sort(
        (left, right) => right.totalBilling - left.totalBilling
      )[0];
      return topBrand?.brand ?? null;
    });
  }, [activeBrandCards]);

  if (!canLoad) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-sm text-muted-foreground">
        Select a company to view active and expiring license overview.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CustomerLicenseTrendChart
        companyPartnerId={companyPartnerId}
        companyName={companyName}
        companyPartnerIds={selectedCompanyPartnerIds}
        companyNames={selectedCompanyNames}
      />

      <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm md:p-5">
        <div className="mb-4 flex flex-col gap-3 border-b border-border/70 pb-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Active licenses</h2>
            <p className="text-xs text-muted-foreground">
              {isMultiCompany
                ? `${formatNumber(activeRows.length)} active license row(s) grouped by brand for ${companyLabel ?? "selected companies"}`
                : activeData
                ? `${formatNumber(activeData.total)} active license row(s) grouped by brand for ${companyFilter}`
                : `Grouped by brand for ${companyFilter}`}
            </p>
          </div>
          <div className="w-full max-w-xs rounded-2xl border border-border/70 bg-muted/30 px-4 py-3 md:w-auto">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Total Billings (HKD)
            </p>
            <p className="mt-1 text-2xl font-semibold text-foreground">
              {formatNumber(activeTotalBilling)}
            </p>
          </div>
        </div>

        {isOverviewLoading ? (
          <p className="text-xs text-muted-foreground">Loading active licenses...</p>
        ) : overviewError ? (
          <p className="text-xs text-destructive">Failed to load active licenses.</p>
        ) : activeRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No active license records for {companyLabel ?? "the selected companies"}.
          </p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border/70 bg-background">
            <div className="overflow-auto">
              <table className="min-w-full table-fixed border-collapse text-sm">
                <thead className="bg-muted/35">
                  <tr className="border-b border-border/70">
                    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      Brand / Product portfolio
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      Units sold
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      Revenue (HKD)
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      Contribution to brand (%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeBrandCards.map((card) => {
                    const brandShare =
                      activeTotalBilling > 0
                        ? (card.totalBilling / activeTotalBilling) * 100
                        : 0;
                    const isExpanded = expandedBrand === card.brand;

                    return (
                      <React.Fragment key={card.brand}>
                        <tr className="border-b border-border/70 bg-muted/25 align-middle">
                          <td
                            colSpan={3}
                            className="px-4 py-3"
                          >
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 text-left"
                              onClick={() =>
                                setExpandedBrand((current) =>
                                  current === card.brand ? null : card.brand
                                )
                              }
                            >
                              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                              </span>
                              <span className="truncate text-[13px] font-semibold uppercase tracking-[0.22em] text-slate-700">
                                {card.brand}
                              </span>
                              <span className="truncate text-[12px] uppercase tracking-[0.18em] text-muted-foreground">
                                Total brand billing: {formatCurrency(card.totalBilling)}
                              </span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <span className="text-[12px] text-muted-foreground">
                                Overall distributed:
                              </span>
                              <div className="flex items-center gap-3">
                                <div className="h-2.5 w-28 overflow-hidden rounded-full bg-slate-200 md:w-36">
                                  <div
                                    className="h-full rounded-full bg-rose-500"
                                    style={{
                                      width: `${Math.max(brandShare, 1)}%`,
                                    }}
                                  />
                                </div>
                                <span className="w-12 text-right text-[12px] font-semibold text-slate-700">
                                  {formatPercent(brandShare)}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>

                        {isExpanded &&
                          card.products.map((product) => {
                            const productShare =
                              card.totalBilling > 0
                                ? (product.totalBilling / card.totalBilling) * 100
                                : 0;

                            return (
                              <tr
                                key={`${card.brand}-${product.productModel}`}
                                className="border-b border-border/70 bg-background align-middle hover:bg-muted/20"
                              >
                                <td className="px-4 py-4">
                                  <div className="pl-6">
                                    <div className="text-[14px] text-slate-700">
                                      {product.productModel}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-right tabular-nums text-slate-600">
                                  {formatNumber(product.totalSeats)}
                                </td>
                                <td className="px-4 py-4 text-right tabular-nums font-semibold text-slate-800">
                                  {formatCurrency(product.totalBilling)}
                                </td>
                                <td className="px-4 py-4 text-right">
                                  <div className="flex items-center justify-end gap-3">
                                    <div className="h-2.5 w-28 overflow-hidden rounded-full bg-slate-200 md:w-36">
                                      <div
                                        className="h-full rounded-full bg-indigo-500"
                                        style={{
                                          width: `${Math.max(productShare, 1)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="w-12 text-right text-[12px] font-semibold text-slate-700">
                                      {formatPercent(productShare)}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm md:p-5">
        <div className="mb-4 flex flex-col gap-1 border-b border-border/70 pb-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Expiring soon (next 90 days)
            </h2>
            <p className="text-xs text-muted-foreground">
              Upcoming warranty end dates for {companyLabel ?? companyFilter}
            </p>
          </div>
        </div>

        {isOverviewLoading ? (
          <p className="text-xs text-muted-foreground">Loading expiring licenses...</p>
        ) : overviewError ? (
          <p className="text-xs text-destructive">
            Failed to load expiring licenses.
          </p>
        ) : expiringRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No expiring-soon records for {companyLabel ?? "the selected companies"}.
          </p>
        ) : (
          <>
            <div className="overflow-auto rounded-xl border border-border/70">
              <table className="min-w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      Warranty end date
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      Brand
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      Product model
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                      PIP
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                      Sum qty
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                      PIP total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleExpiringRows.map((item, index) => (
                    <tr
                      key={`${item.warranty_end_date}-${displayValueFromUnknown(item.brand_grouping, "Others")}-${displayValueFromUnknown(item.product_model, "na")}-${index}`}
                      className="border-t border-border/70 bg-background"
                    >
                      <td className="px-4 py-3">
                        {formatDate(item.warranty_end_date)}
                      </td>
                      <td className="px-4 py-3">
                        {displayValueFromUnknown(item.brand_grouping, "Others")}
                      </td>
                      <td className="px-4 py-3">
                        {displayValueFromUnknown(
                          item.product_model,
                          "Unspecified product"
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatNumber(item.pip)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatNumber(item.sum_qty)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatNumber(item.sum_pip_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <SimplePager
              page={expiringPage}
              pageSize={expiringPageSize}
              total={isMultiCompany ? expiringRows.length : expiringData?.pagination.total ?? 0}
              onChange={(nextPage, nextPageSize) => {
                setExpiringPage(nextPage);
                setExpiringPageSize(nextPageSize);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerLicenseOverview;
